import {
  Edit,
  SimpleForm,
  TextInput,
  DateInput,
  ReferenceManyField,
  ArrayField,
  ChipField,
  Datagrid,
  DateField,
  List,
  ReferenceField,
  SingleFieldList,
  TextField,
  EditButton,
  required,
  ImageField,
  Create,
  TabbedForm,
  FormTab,
  ArrayInput,
  SimpleFormIterator,
  Button,
  FileInput,
  FileField,
  FormDataConsumer,
  TopToolbar,
  FilterButton,
  CreateButton,
  useListContext,
  FunctionField,
} from "react-admin";
import ColorInput from "../../components/color/color-input";
import ImageInput from "../../components/image/image-input";
import ModuleEdit from "./Module";
import DataGridExtended from "../../components/list/data.grid.extended";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider, 
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Checkbox,
  Chip as MuiChip
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNotify } from "react-admin";
import { BrowseParentApplications } from "./BrowseParentApplications";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";

const ApplicationListActions = () => {
  const { filterValues, setFilters } = useListContext();
  const showDerived = filterValues._includeDerived === true;
  const [openBrowseDialog, setOpenBrowseDialog] = useState(false);
  
  const toggleDerived = (checked: boolean) => {
    if (checked) {
      setFilters({ ...filterValues, _includeDerived: true }, {});
    } else {
      const { _includeDerived, ...rest } = filterValues;
      setFilters(rest, {});
    }
  };
  
  return (
    <>
      <TopToolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showDerived}
                onChange={(e) => toggleDerived(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Show Derived Applications
              </Typography>
            }
          />
          <Button
            onClick={() => setOpenBrowseDialog(true)}
            label="Browse Parent Apps"
            startIcon={<CloudDownloadIcon />}
            variant="outlined"
          />
          <CreateButton />
        </Box>
      </TopToolbar>
      
      <BrowseParentApplications
        open={openBrowseDialog}
        onClose={() => setOpenBrowseDialog(false)}
      />
    </>
  );
};

export const ApplicationList = () => {
  return (
    <List 
      resource="application" 
      actions={<ApplicationListActions />}
    >
      <DataGridExtended rowClick="edit" title={"Applications"}>
        <TextField source="name" />
        <TextField source="description" />
        <TextField source="key" />
        <FunctionField
          label="Version"
          render={(record: any) => {
            const version = record.currentVersion || 1;
            const label = record.versionLabel || `v${version}`;
            return (
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {label}
              </Typography>
            );
          }}
        />
        <FunctionField
          label="Status"
          render={(record: any) => {
            // Check if rebuild is required
            if (record.configuration?.requiresRegeneration) {
              return (
                <MuiChip 
                  label="Rebuild Required" 
                  size="small" 
                  color="warning"
                  variant="filled"
                />
              );
            }
            
            // Check if UI needs compilation
            if (record.configuration?.hasUI && !record.configuration?.isUICompiled) {
              return (
                <MuiChip 
                  label="Needs Build" 
                  size="small" 
                  color="warning"
                  variant="outlined"
                />
              );
            }
            
            // Check if published
            if (record.published) {
              return (
                <MuiChip 
                  label="Published" 
                  size="small" 
                  color="success"
                  variant="filled"
                />
              );
            }
            
            // Draft status
            return (
              <MuiChip 
                label="Draft" 
                size="small" 
                color="default"
                variant="outlined"
              />
            );
          }}
        />
        <FunctionField
          label="Type"
          render={(record: any) => {
            if (record.derivedBy) {
              return (
                <MuiChip 
                  label={`Derived from ${record.derivedBy.entityType}`} 
                  size="small" 
                  color="secondary"
                  variant="outlined"
                />
              );
            }
            return <MuiChip label="Standard" size="small" color="primary" />;
          }}
        />
      </DataGridExtended>
    </List>
  );
};

// Transform function to handle imported application data
const createApplicationTransform = (creationMethod: string, notify: any) => (data: any) => {
    console.log('🔍 Create transform called with data:', data);
    
    // If importing from a previous export, extract and restore all fields
    if (creationMethod === 'import' && data.importedConfiguration?.rawFile) {
      try {
        const fileReader = new FileReader();
        return new Promise((resolve, reject) => {
          fileReader.onload = (event) => {
            try {
              const importedData = JSON.parse(event.target?.result as string);
              console.log('📥 Imported application data:', importedData);
              
              // Create the application data structure from the imported file
              const restoredData = {
                name: importedData.name,
                key: importedData.key,
                description: importedData.description,
                configuration: importedData.configuration,
                openApiSpec: importedData.openApiSpec,
                sourceCodeStoreId: importedData.sourceCodeStoreId, // Restore source code reference
                // Convert base64 back to transient property for processing
                compiledZipFile: importedData.compiledUIDataBase64, // Restore compiled UI as transient property
                // Preserve any original form data that wasn't in the export
                ...data
              };
              
              console.log('✅ Restored application data:', {
                name: restoredData.name,
                hasConfiguration: !!restoredData.configuration,
                hasOpenApiSpec: !!restoredData.openApiSpec,
                hasSourceCodeStoreId: !!restoredData.sourceCodeStoreId,
                hasCompiledUIData: !!restoredData.compiledZipFile
              });
              
              resolve(restoredData);
            } catch (error) {
              console.error('❌ Failed to parse imported application:', error);
              reject(new Error('Invalid application export file'));
            }
          };
          fileReader.onerror = () => reject(new Error('Failed to read import file'));
          fileReader.readAsText(data.importedConfiguration.rawFile);
        });
      } catch (error) {
        console.error('❌ Import processing error:', error);
        notify('Failed to process import file', { type: 'error' });
        return data;
      }
    }
    
    return data;
  };

const ApplicationCreateForm = () => {
  const [creationMethod, setCreationMethod] = useState('files');
  const notify = useNotify();
  const [openBrowseDialog, setOpenBrowseDialog] = useState(false);

  return (
    <>
      <SimpleForm>
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardHeader title="Create Application" />
          <CardContent>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Creation Method</FormLabel>
              <RadioGroup
                value={creationMethod}
                onChange={(e) => setCreationMethod(e.target.value)}
                row
              >
                <FormControlLabel 
                  value="files" 
                  control={<Radio />} 
                  label="From Files (HAR, OpenAPI, etc.)" 
                />
                <FormControlLabel 
                  value="import" 
                  control={<Radio />} 
                  label="Import Application" 
                />
                <FormControlLabel 
                  value="manual" 
                  control={<Radio />} 
                  label="Manual Creation" 
                />
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>
      </Box>

      {/* Basic Application Info - Always shown */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Application Details" />
        <CardContent>
          <TextInput label="Name" source="name" fullWidth sx={{ mb: 2 }} />
          <TextInput label="Description" source="description" fullWidth multiline sx={{ mb: 2 }} />
          <TextInput label="Key" source="key" fullWidth />
        </CardContent>
      </Card>

      {/* File-based Creation */}
      {creationMethod === 'files' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title="Import from Files" 
            subheader="Create application from HAR files, OpenAPI specs, or configuration schemas"
          />
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              You can combine multiple file types. HAR files and OpenAPI specs define the functions, 
              while JSON Schema defines what's configurable.
            </Alert>
            <FileInput source="harFile" label="HAR File" accept=".har" sx={{ mb: 2 }}>
              <FileField source="title" title="title" />
            </FileInput>
            <FileInput source="openApiSpec" label="Open API Spec" accept=".json,.yaml,.yml" sx={{ mb: 2 }}>
              <FileField source="title" title="title" />
            </FileInput>
            <FileInput source="jsonSchema" label="Configuration JSON Schema" accept=".json" sx={{ mb: 2 }}>
              <FileField source="title" title="title" />
            </FileInput>
          </CardContent>
        </Card>
      )}

      {/* Application Import */}
      {creationMethod === 'import' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title="Import Application" 
            subheader="Import from parent platform or upload an export file"
          />
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Button
                onClick={() => setOpenBrowseDialog(true)}
                label="Browse Parent Platform Applications"
                startIcon={<CloudDownloadIcon />}
                variant="contained"
                color="primary"
              />
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                Connect to your parent Neo platform instance and import published applications
              </Typography>
            </Box>
            
            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="textSecondary">OR Upload Export File</Typography>
            </Divider>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Import a complete application definition including all functions, configurations, settings, and UI assets.
            </Alert>
            <FileInput 
              source="importedConfiguration" 
              label="Application Export File" 
              accept=".json"
            >
              <FileField source="title" title="title" />
            </FileInput>
            <FormDataConsumer>
              {({ formData }) => {
                // Show preview of what will be imported
                if (formData?.importedConfiguration?.rawFile) {
                  return (
                    <Box sx={{ 
                      mt: 2, 
                      p: 2, 
                      bgcolor: '#e8f5e9', 
                      borderRadius: 1,
                      border: '1px solid #4caf50'
                    }}>
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'medium' }}>
                        📁 Ready to import: <strong>{formData.importedConfiguration.rawFile.name}</strong>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#1b5e20', mt: 0.5 }}>
                        The import will be processed when you save the application.
                      </Typography>
                    </Box>
                  );
                }
                return null;
              }}
            </FormDataConsumer>
          </CardContent>
        </Card>
      )}

      {/* Manual Creation */}
      {creationMethod === 'manual' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title="Manual Creation" 
            subheader="Create an empty application and add functions manually"
          />
          <CardContent>
            <Alert severity="info">
              This will create an empty application. You can add functions and configurations 
              after creation using the application editor.
            </Alert>
          </CardContent>
        </Card>
      )}
    </SimpleForm>
    
    <BrowseParentApplications
      open={openBrowseDialog}
      onClose={() => setOpenBrowseDialog(false)}
    />
  </>
  );
};

export const ApplicationCreate = (props) => {
  const [creationMethod, setCreationMethod] = useState('files');
  const notify = useNotify();

  return (
    <Create {...props} transform={createApplicationTransform(creationMethod, notify)}>
      <ApplicationCreateForm />
    </Create>
  );
};